import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import uuid from "uuid";
import styled from "styled-components";
import {
  changeAssetType,
  initialize,
  postToFacebook,
  postToTwitter,
  saveToCamio
} from "../../actions/publish";
import FacebookApi, { FacebookResponseTypes } from "../../api/facebook";
import TwitterApi, { TwitterResponseTypes } from "../../api/twitter";
import TimecodeDisplay from "../../components/Editor/TimecodeDisplay";
import { actions as snackbarActions } from "../../actions/snackbar";
import { makeGetConfigValue } from "../../selectors/config";
import ASSET_TYPE from "../../constants/publishingAssetTypes";
import Timecode from "../../util/timecode";
import WebSocketHelper from "../../util/websocketHelper";
import localize from "../../util/localizeUtils";
import DraggablePopup from "../DraggablePopup";
import Tabs, { PUBLISH_TABS } from "./Tabs";
import FacebookTabContent from "./FacebookTabContent";
import TwitterTabContent from "./TwitterTabContent";
import { ErrorMessage } from "./styled-components";
import Settings from "../../settings";

const fbPermissions = ["manage_pages", "publish_pages"];

const FieldLabel = styled.div`
  display: inline-block;
  width: 150px;
`;

const StoryNameField = styled.div`
  display: flex;
  flexdirection: row;
  fontsize: 11px;
  padding: 12px;
`;

const PublishAsField = styled.div`
  display: flex;
  flexdirection: row;
  height: 20px;
  padding: 0 12px;
`;

const AssetTypeRadioContainer = styled.div`
  alignitems: center;
  display: flex;
  flex: 1;
`;

function TextField({ disabled, placeholder, value, onChange }) {
  return (
    <input
      style={{ padding: "1px 6px", flex: 1 }}
      type="text"
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  );
}

TextField.propTypes = {
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func
};

TextField.defaultProps = {
  disabled: false,
  placeholder: "",
  value: "",
  onChange: () => {}
};

function RadioButton({ checked, disabled, text, onChange }) {
  return (
    <span style={{ alignItems: "center", display: "flex" }}>
      <input
        type="radio"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        value={text.toLowerCase()}
      />
      <span style={{ paddingLeft: "4px", paddingRight: "16px" }}>{text}</span>
    </span>
  );
}

RadioButton.propTypes = {
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

class PublishingDialog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.wsHelper = null;

    if (props.socialMediaApiUrl) {
      this.wsHelper = new WebSocketHelper(
        props.socialMediaApiUrl,
        !props.publishingEnabled
      );
    }

    this.state = {
      activeTab: PUBLISH_TABS.facebook,
      completedTabs: [],
      duration: 5,
      errorMessage: "",
      fbAuthResponse: {},
      facebookUser: null,
      timecodeDuration: "00:00:05:00",
      title: "",
      twitterUser: null
    };
  }

  UNSAFE_componentWillMount() {
    if (this.props.clipperMovie) {
      this.props.onChangeAssetType(ASSET_TYPE.movie.toLowerCase());
      this.setState({ timecodeDuration: this.props.clipperDuration });
    }
  }

  componentDidMount() {
    if (
      Settings.socialMediaIntegrations.facebook &&
      this.props.publishingEnabled
    ) {
      this.facebookApi = new FacebookApi(
        this.wsHelper,
        this.props.socialMediaApiUrl,
        this.onFacebookResponse
      );
    }
    if (
      Settings.socialMediaIntegrations.twitter &&
      this.props.publishingEnabled
    ) {
      this.twitterApi = new TwitterApi(
        this.wsHelper,
        this.props.socialMediaApiUrl,
        this.onTwitterResponse
      );
    }
    this.props.initialize(this.props.assetType);
  }

  componentWillUnmount() {
    if (this.wsHelper) {
      this.wsHelper.destroy();
    }
  }

  onFacebookResponse = response => {
    if (response.type === FacebookResponseTypes.AUTH) {
      if (response.error) {
        this.setState({ fbAuthResponse: response.data });
      } else {
        this.setState({ facebookUser: response.data });
      }
    } else if (response.type === FacebookResponseTypes.IMAGE_UPLOAD) {
      this.setState({ imageResponse: response.error || response.data });
    } else if (response.type === FacebookResponseTypes.VIDEO_UPLOAD) {
      this.setState({ videoResponse: response.error || response.data });
    }
  };

  onTwitterResponse = response => {
    if (response.type === TwitterResponseTypes.AUTH) {
      if (response.error) {
        this.setState({ twitterAuthResponse: response.data });
      } else {
        this.setState({ twitterUser: response.data });
      }
    } else if (response.type === TwitterResponseTypes.TWEET) {
      this.setState({ mediaResponse: response.error || response.data });
    }
  };

  onChangeAssetType = event => {
    this.props.onChangeAssetType(event.target.value);
  };

  onChangeTitle = event => {
    this.setState({ errorMessage: "", title: event.target.value });
  };

  onChangeTimeCode = newTimeCode => {
    const duration = Timecode.convertToSeconds(
      newTimeCode,
      this.props.frameRate
    );
    this.setState({ duration, timecodeDuration: newTimeCode });
  };

  onSelectTab = tab => {
    this.setState({ activeTab: tab, errorMessage: "" });
  };

  // FACEBOOK
  onLogInToFacebook = () => {
    this.facebookApi
      .loginDialog(fbPermissions)
      .then(() => this.setState({ fbAuthResponse: {} }))
      .catch(error => {
        this.props.showSnack(uuid.v4(), {
          content: error,
          timeout: 7000,
          buttonTemplate: localize("dismissButton")
        });
        this.setState({ fbAuthResponse: error });
      });
  };

  onLogOutOfFacebook = () => {
    this.facebookApi
      .revokeLogin()
      .then(() => this.setState({ fbAuthResponse: {} }))
      .catch(error => {
        this.props.showSnack(uuid.v4(), {
          content: error,
          timeout: 7000,
          buttonTemplate: localize("dismissButton")
        });
        this.setState({ fbAuthResponse: error });
      });
  };

  onChangeFacebookUser = () => {
    this.facebookApi
      .revokeLogin()
      .then(() => {
        this.facebookApi.logout();
        return this.facebookApi
          .loginDialog(fbPermissions)
          .then(() => this.setState({ fbAuthResponse: {} }))
          .catch(error => {
            this.props.showSnack(uuid.v4(), {
              content: error,
              timeout: 7000,
              buttonTemplate: localize("dismissButton")
            });
            this.setState({ fbAuthResponse: error });
          });
      })
      .catch(error => this.setState({ fbAuthResponse: error }));
  };

  onPostToFacebook = (caption, pageId) => {
    if (!this.state.title) {
      this.setState({ errorMessage: localize("requiredStoryNameError") });
    } else if (
      this.state.facebookUser.permissions.some(p => p.status !== "granted")
    ) {
      this.facebookApi
        .loginDialog(fbPermissions, true)
        .then(() => this.setState({ fbAuthResponse: {} }))
        .catch(error => {
          this.props.showSnack(uuid.v4(), {
            content: error,
            timeout: 7000,
            buttonTemplate: localize("dismissButton")
          });
          this.setState({ fbAuthResponse: error });
        });
    } else {
      this.props.onPostToFacebook(
        this.facebookApi,
        this.state.title,
        caption,
        this.state.duration,
        pageId
      );
      this.props.showSnack(uuid.v4(), {
        content: localize("facebookPostSuccess"),
        timeout: 7000,
        buttonTemplate: localize("dismissButton")
      });
      this.setState({
        completedTabs: [...this.state.completedTabs, PUBLISH_TABS.facebook]
      });
    }
  };

  // TWITTER
  onLogInToTwitter = () => {
    this.twitterApi
      .loginDialog(true)
      .then(() => this.setState({ twitterAuthResponse: {} }))
      .catch(error => {
        this.props.showSnack(uuid.v4(), {
          content: error,
          timeout: 7000,
          buttonTemplate: localize("dismissButton")
        });
        this.setState({ twitterAuthResponse: error });
      });
  };

  onLogOutOfTwitter = () => {
    this.setState({ twitterUser: null });
    this.twitterApi.logout();
  };

  onChangeTwitterUser = () => {
    this.twitterApi.logout();
    this.twitterApi
      .loginDialog(true)
      .then(() => this.setState({ twitterAuthResponse: {} }))
      .catch(error => {
        this.props.showSnack(uuid.v4(), {
          content: error,
          timeout: 7000,
          buttonTemplate: localize("dismissButton")
        });
        this.setState({ twitterAuthResponse: error });
      });
  };

  onPostToTwitter = caption => {
    if (!this.state.title) {
      this.setState({ errorMessage: localize("requiredStoryNameError") });
    } else {
      this.props.onPostToTwitter(
        this.twitterApi,
        this.state.title,
        caption,
        this.state.duration
      );
      this.props.showSnack(uuid.v4(), {
        content: localize("twitterPostSuccess"),
        timeout: 7000,
        buttonTemplate: localize("dismissButton")
      });
      this.setState({
        completedTabs: [...this.state.completedTabs, PUBLISH_TABS.twitter]
      });
    }
  };

  onSaveToFile = () => {
    this.setState({
      completedTabs: [...this.state.completedTabs, PUBLISH_TABS.saveAs]
    });
  };

  onSaveToLuci = () => {
    if (!this.state.title) {
      this.setState({ errorMessage: localize("requiredStoryNameError") });
    } else {
      this.props.onSaveToCamio(this.state.title, this.state.duration);
      this.setState({
        completedTabs: [...this.state.completedTabs, PUBLISH_TABS.luci]
      });
    }
  };

  renderDurationField() {
    if (this.props.assetType === ASSET_TYPE.image.toLowerCase()) {
      return null;
    }

    return (
      <div style={{ alignItems: "center", display: "flex" }}>
        <TimecodeDisplay
          disabled={!!this.props.clipperMovie}
          label={localize("durationLabel")}
          timecode={this.state.timecodeDuration}
          timecodeChanged={this.onChangeTimeCode}
          frameRate={this.props.frameRate}
        />
      </div>
    );
  }

  renderAssetTypeRadio = () => (
    <AssetTypeRadioContainer>
      <RadioButton
        checked={this.props.assetType === ASSET_TYPE.image.toLowerCase()}
        disabled={!!this.props.clipperMovie}
        text={ASSET_TYPE.image}
        onChange={this.onChangeAssetType}
      />
      <RadioButton
        checked={this.props.assetType === ASSET_TYPE.movie.toLowerCase()}
        disabled={!!this.props.clipperMovie}
        text={ASSET_TYPE.movie}
        onChange={this.onChangeAssetType}
      />
    </AssetTypeRadioContainer>
  );

  renderTabContent() {
    switch (this.state.activeTab) {
      case PUBLISH_TABS.facebook: {
        const loggedInUser = this.state.facebookUser
          ? this.state.facebookUser.name
          : "";
        const pages = this.state.facebookUser
          ? this.state.facebookUser.pages
          : [];
        return (
          <FacebookTabContent
            loggedInUser={loggedInUser}
            onChangeUser={this.onChangeFacebookUser}
            onLogIn={this.onLogInToFacebook}
            onLogOut={this.onLogOutOfFacebook}
            onPost={this.onPostToFacebook}
            pages={pages}
          />
        );
      }
      case PUBLISH_TABS.twitter: {
        const loggedInUser = this.state.twitterUser
          ? this.state.twitterUser.screenName
          : "";
        return (
          <TwitterTabContent
            loggedInUser={loggedInUser}
            onChangeUser={this.onChangeTwitterUser}
            onLogIn={this.onLogInToTwitter}
            onLogOut={this.onLogOutOfTwitter}
            onPost={this.onPostToTwitter}
          />
        );
      }
      default:
        return null;
    }
  }

  render() {
    return (
      <DraggablePopup
        visible={this.props.isOpen}
        title={localize("publishTitle")}
        handle=".handle"
        height={324}
        width={600}
        closeDialog={this.props.closeDialog}
      >
        <div>
          <StoryNameField>
            <FieldLabel>{localize("storyNameLabel")}</FieldLabel>
            <TextField
              placeholder={localize("storyNamePlaceholder")}
              value={this.state.title}
              onChange={this.onChangeTitle}
            />
          </StoryNameField>
          <PublishAsField>
            <FieldLabel>{localize("publishAsLabel")}</FieldLabel>
            <div style={{ alignItems: "center", display: "flex", flex: 1 }}>
              <RadioButton
                checked={
                  this.props.assetType === ASSET_TYPE.image.toLowerCase()
                }
                disabled={!!this.props.clipperMovie}
                text={ASSET_TYPE.image}
                onChange={this.onChangeAssetType}
              />
              <RadioButton
                checked={
                  this.props.assetType === ASSET_TYPE.movie.toLowerCase()
                }
                disabled={!!this.props.clipperMovie}
                text={ASSET_TYPE.movie}
                onChange={this.onChangeAssetType}
              />
            </div>
            {this.renderDurationField()}
          </PublishAsField>
          <Tabs
            activeTab={this.state.activeTab}
            completedTabs={this.state.completedTabs}
            onSelectTab={this.onSelectTab}
          />
          {this.renderTabContent()}
          <ErrorMessage
            style={{ marginLeft: 16, marginTop: -12 }}
            visible={!!this.state.errorMessage}
          >
            {this.state.errorMessage}
          </ErrorMessage>
        </div>
      </DraggablePopup>
    );
  }
}

PublishingDialog.propTypes = {
  assetType: PropTypes.string.isRequired,
  clipperDuration: PropTypes.string,
  clipperMovie: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  socialMediaApiUrl: PropTypes.string.isRequired,
  closeDialog: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  onChangeAssetType: PropTypes.func.isRequired,
  onPostToFacebook: PropTypes.func.isRequired,
  onPostToTwitter: PropTypes.func.isRequired,
  onSaveToCamio: PropTypes.func.isRequired,
  frameRate: PropTypes.number.isRequired,
  showSnack: PropTypes.func.isRequired,
  publishingEnabled: PropTypes.bool.isRequired
};

PublishingDialog.defaultProps = {
  clipperDuration: "00:00:00:00",
  clipperMovie: ""
};

const makeMapStateToProps = () => {
  const getConfigValue = makeGetConfigValue();

  const mapStateToProps = state => ({
    frameRate: parseInt(
      getConfigValue(state, "luci", "automationTimecodeFrameRates", "30"),
      10
    ),
    publishingEnabled:
      getConfigValue(state, "userConfig", "publishDisplay", "show") === "show",
    assetType: state.publish.assetType,
    clipperDuration: state.clipperMovie.duration,
    clipperMovie: state.clipperMovie.fileUrl,
    socialMediaApiUrl:
      state.app.config.publishing && state.app.config.publishing.server
        ? `http://${state.app.config.publishing.server}:${state.app.config.publishing.smapiPort}`
        : ""
  });

  return mapStateToProps;
};

const mapDispatchToProps = dispatch => ({
  initialize: assetType => {
    dispatch(initialize(assetType));
  },
  onChangeAssetType: assetType => {
    dispatch(changeAssetType(assetType));
  },
  onPostToFacebook: (facebookApi, title, description, duration, pageId) => {
    dispatch(postToFacebook(facebookApi, title, description, duration, pageId));
  },
  onPostToTwitter: (twitterApi, title, description, duration) => {
    dispatch(postToTwitter(twitterApi, title, description, duration));
  },
  onSaveToCamio: (title, duration) => {
    dispatch(saveToCamio(title, duration));
  },
  showSnack: (id, data) => {
    dispatch(snackbarActions.showSnack(id, data));
  }
});

export default connect(
  makeMapStateToProps,
  mapDispatchToProps
)(PublishingDialog);



// WEBPACK FOOTER //
// ./app/components/PublishingDialog/index.js